// Each changed file's review status as last computed, kept in this browser
// so that reloading the Files view shows the explorer's review icons, the
// file header's count and the PR meter at once, before the files are
// diffed again (see DiffModel.progress).
//
// One record per comparison: the pull request, which comparison it is
// (base…head, or an interdiff's commits), the diff options and the engine
// build. A file's entry is keyed by its blobs and patch, which with those
// fix its blocks: a file whose text changed is never shown with another
// text's status, a file a push left alone keeps its status, and each entry
// names the
// marks it was computed for (`FileStatus.marks`, a hash of the anchors on
// the file's paths): a mark is content on one path, so it changes only that
// file's status, and every other file's kept status stays good.
//
// Records live in the signed-in account's IndexedDB (api/db.ts, the `kv`
// store), which is deleted at sign-out and when another account signs in.
// Each write prunes records past STATUS_MAX_AGE_MS or beyond the newest
// STATUS_KEPT, found through an index record (STATUS_INDEX).
import { idbDelete, idbGet, idbPut } from '../api/db';
import { anchorId, type Anchor } from '../review/anchors';

/** A changed file's review status: its removed and added lines, how many the marks cover, and which change blocks are reviewed, in order. */
export interface FileStatus {
  total: number;
  done: number;
  reviewed: boolean[];
  /** `marksSignature` of the marks on the file's paths it was computed for. */
  marks: string;
}

export interface StatusRecord {
  /** The record's format: 2 since statuses are kept per file's marks. */
  v: 2;
  /** When it was written (epoch ms), for pruning. */
  at: number;
  /** By `fileIdentity`. */
  files: Record<string, FileStatus>;
  /** By `fileIdentity`: `marksSignature` of the pending key-based marks the
   *  file was looked through for and held none of, so a reload does not
   *  look again while the same keys are pending. */
  dead?: Record<string, string>;
}

/** Where records are kept. Either method may fail (storage full or unavailable); callers carry on without it. */
export interface StatusStore {
  read(key: string): Promise<StatusRecord | undefined>;
  write(key: string, record: StatusRecord): Promise<void>;
}

const PREFIX = 'review-status|';
/** Records kept at most, newest first. */
export const STATUS_KEPT = 200;
/** Age past which a record is dropped (30 days). */
export const STATUS_MAX_AGE_MS = 30 * 24 * 60 * 60_000;

/** Of `entries` (key, record), the keys to drop: unstamped ones, those past STATUS_MAX_AGE_MS, and all but the newest STATUS_KEPT. */
export function staleStatusKeys(entries: [string, { at?: number }][], now: number): string[] {
  const stamped = entries.filter(([, r]) => typeof r.at === 'number' && now - r.at! <= STATUS_MAX_AGE_MS).sort(([, a], [, b]) => b.at! - a.at!);
  const keep = new Set(stamped.slice(0, STATUS_KEPT).map(([k]) => k));
  return entries.map(([k]) => k).filter((k) => !keep.has(k));
}

/** The record listing every kept record's key (without PREFIX) and write time, so a write prunes without reading the records. */
export const STATUS_INDEX = 'review-status-index';

/** The key-value storage a status store keeps its records in. */
export interface StatusKv {
  get(key: string): Promise<unknown>;
  put(key: string, value: unknown): Promise<unknown>;
  delete(key: string): Promise<unknown>;
}

/**
 * A status store over `kv`: each write stores its record and updates the
 * index (one read), then deletes the records `staleStatusKeys` drops.
 */
export function kvStatusStore(kv: StatusKv, now: () => number = Date.now): StatusStore {
  return {
    async read(key) {
      const v = await kv.get(PREFIX + key);
      return isRecord(v) ? v : undefined;
    },
    async write(key, record) {
      await kv.put(PREFIX + key, record);
      const stored = await kv.get(STATUS_INDEX);
      const index: Record<string, number> = stored && typeof stored === 'object' ? { ...(stored as Record<string, number>) } : {};
      index[key] = record.at;
      const stale = staleStatusKeys(
        Object.entries(index).map(([k, at]) => [k, { at }]),
        now(),
      );
      for (const k of stale) {
        delete index[k];
        await kv.delete(PREFIX + k);
      }
      await kv.put(STATUS_INDEX, index);
    },
  };
}

export const idbStatusStore: StatusStore = kvStatusStore({
  get: (k) => idbGet('kv', k),
  put: (k, v) => idbPut('kv', k, v),
  delete: (k) => idbDelete('kv', k),
});

function isRecord(v: unknown): v is StatusRecord {
  if (!v || typeof v !== 'object') return false;
  const r = v as Partial<StatusRecord>;
  return r.v === 2 && typeof r.at === 'number' && !!r.files && typeof r.files === 'object';
}

/**
 * A stable 64-bit hash of `s` (hex), as two 32-bit lanes with different
 * mixing (FNV-1a and a multiply-xorshift): n / p skip files on what it
 * names, so a collision must be out of reach. Cheap enough to run on every
 * change of marks.
 */
export function hash64(s: string): string {
  let a = 0x811c9dc5;
  let b = 0x9747b28c;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b ^ c, 0x5bd1e995);
    b ^= b >>> 15;
  }
  b = Math.imul(b ^ (b >>> 13), 0xc2b2ae35) ^ s.length;
  return (a >>> 0).toString(16).padStart(8, '0') + ((b ^ (b >>> 16)) >>> 0).toString(16).padStart(8, '0');
}

const hash = hash64;

/** Names a set of review marks (their anchor IDs): the same for the same marks, whatever their order. */
export function marksSignature(ids: Iterable<string>): string {
  const keys = [...ids].sort();
  return `${keys.length}.${hash(keys.join('\n'))}`;
}

/** The engine build the statuses were computed by: a new build lays changes out anew. */
export const ENGINE_BUILD: string = import.meta.env.VITE_ENGINE_BUILD ?? 'unknown';

/**
 * The key of a comparison's record: its subject's key, which comparison
 * (`from`…`to`, empty for base…head), the diff options and the engine
 * build. The Files view writes under it; the Branches tab reads the same
 * record for a branch's compare (or its pull request) as the Files view
 * opens it.
 */
export function statusKeyOf(subject: string, mode: { from: string | null; to: string | null; algorithm: string; ignoreWhitespace: boolean }): string {
  return [subject, mode.from ?? '', mode.to ?? '', mode.algorithm, mode.ignoreWhitespace ? 'ws' : '', ENGINE_BUILD].join('|');
}

/** Names one changed file's text on both sides: its path, blobs and patch. */
export function fileIdentity(e: { path: string; oldSha: string | null; newSha: string | null; patch: string | null }): string {
  return `${e.path}|${e.oldSha ?? ''}|${e.newSha ?? ''}|${e.patch === null ? '' : hash(e.patch)}`;
}

/**
 * A changed file's identity in a base…head comparison, as the Files view
 * keeps its status and the Branches tab looks it up: GitHub's listing names
 * no old blob (the patch fixes the old side), so it has none; a removed
 * file has no new one.
 */
export function baseHeadIdentity(f: { path: string; status: string; newSha: string | null; patch: string | null }): string {
  return fileIdentity({ path: f.path, oldSha: null, newSha: f.status === 'removed' ? null : f.newSha, patch: f.patch });
}

/** `marksSignature` of the marks in `marks` on any of `paths`, any blob: what a kept status was computed for. */
export function pathMarksSignature(marks: readonly Anchor[], paths: Iterable<string>): string {
  const on = new Set(paths);
  return marksSignature(marks.filter((m) => on.has(m.path)).map(anchorId));
}
